export type TStatementSetupFeeData = Record<string, string | number> & {
	fx_fee: number;
	setup_fee: number;
	refund_fee: number;
	monthly_fee: number;
	statement_day: string;
	t_cut_off_day?: number;
	chargeback_fee: number;
	withdrawal_fee: number;
	statement_period: string;
};

export interface TStatementRollingFeeData {
	cap: boolean;
	period: number;
	volume: number;
	reserved: number;
}

type TStatementFeeData<T> = T | { setup?: T };

export interface IStatementFees {
	setup?: {
		data?: TStatementFeeData<TStatementSetupFeeData>;
		activated_from?: string | null;
	} | null;
	rollings?: {
		data?: TStatementFeeData<TStatementRollingFeeData>;
		activated_from?: string | null;
	} | null;
}

const unwrapStatementFeeData = <T,>(data: TStatementFeeData<T> | undefined): T | undefined => {
	if (!data) return undefined;
	if (typeof data === "object" && "setup" in data) return data.setup;
	return data as T;
};

export const getStatementSetupFeeData = (fees: IStatementFees) => unwrapStatementFeeData(fees.setup?.data);

export const getStatementRollingFeeData = (fees: IStatementFees) => unwrapStatementFeeData(fees.rollings?.data);

export const isVisibleFeeValue = (value: unknown) => value !== undefined && value !== null && value !== "" && (typeof value !== "number" || value !== 0);

export const getVisibleSetupFeeEntries = (fees: IStatementFees) => {
	const setupData = getStatementSetupFeeData(fees);
	if (!setupData) return [];

	const isDaily = String(setupData.statement_period ?? "").trim().toLowerCase() === "daily";

	return Object.entries(setupData)
		.filter(([key, value]) => isVisibleFeeValue(value) && !(isDaily && (key === "statement_day" || key === "t_cut_off_day")))
		.map(([key, value]) => {
			if (isDaily && key === "statement_period" && setupData.t_cut_off_day !== undefined) {
				return [key, `T+${setupData.t_cut_off_day}`] as const;
			}

			return [key, value] as const;
		});
};

export const hasVisibleRollingReserveFees = (fees: IStatementFees) => {
	const rollings = fees.rollings;
	if (!rollings) return false;
	const rollingData = getStatementRollingFeeData(fees);

	return Boolean(
		rollings.activated_from ||
			isVisibleFeeValue(rollingData?.reserved) ||
			isVisibleFeeValue(rollingData?.period) ||
			isVisibleFeeValue(rollingData?.volume) ||
			isVisibleFeeValue(rollingData?.cap),
	);
};

export const hasVisibleStatementFees = (fees: IStatementFees | undefined) =>
	Boolean(fees && (getVisibleSetupFeeEntries(fees).length > 0 || hasVisibleRollingReserveFees(fees)));

// Keep statement timestamp interpretation identical in the screen and both PDF exports.
export const normalizeUtcDateTime = (value?: string | Date | null) => {
	if (!value) return "";
	const normalized = String(value).trim().replace("T", " ");
	return normalized.replace(/\.\d+Z?$/, "").replace(/Z$/, "").replace(/[+-]\d{2}:?\d{2}$/, "").trim();
};

export const parseUtcDate = (value?: string | Date | null) => {
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

	const normalized = normalizeUtcDateTime(value);
	if (!normalized) return null;

	const isoValue = normalized.includes(" ") ? normalized.replace(" ", "T") : normalized;
	const date = new Date(`${isoValue}${isoValue.includes("T") ? "Z" : "T00:00:00Z"}`);
	return Number.isNaN(date.getTime()) ? null : date;
};
